import React from 'react';
import api from '../../services/api';
import { generateKeyPair, exportPublicKey, toBase64url, wrapPrivateKeyForApp } from '../../utils/fieldEncryption';
import './ShellChrome.css';

// THE SHELL'S OWN ERRANDS — what the frame (Layout) does that is not a page:
// putting the chrome away before a navigation, creating a space from the
// dock, and the Help pane. Kept beside the pages so Layout stays a frame.

/**
 * Puts away whatever chrome stands over the page — the Settings window or the
 * dock (both Radix dialogs, both closed by Escape). Called before a pane
 * navigates, so the person lands on the page rather than behind the window.
 */
export function putAwayChrome() {
    if (typeof document === 'undefined') return;
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
}

/**
 * THE DOCK'S NEW SPACE (parity with nucleus.oS Dock.swift onNewSpace, which
 * opens NewGroupSheet): a group, created the way People › New group creates
 * one — encrypted from birth when the creator has a key — then its photo, if
 * one was picked. The photo is a file the GROUP owns (uploaded into the
 * group, then named by avatar_file_id), the only form PUT /groups accepts.
 *
 * A photo that fails does not fail the space: the group already exists, and
 * a thrown error would leave the form up for a second Create — a duplicate.
 * Returns { group, photoFailed }.
 */
export async function createSpace({ name, description, image }, { encKeys, refreshGroups } = {}) {
    let enc = {};
    try {
        let pub = null;
        if (encKeys?.publicKey) pub = toBase64url(await exportPublicKey(encKeys.publicKey));
        else {
            try { pub = (await api.request('/auth/enc-keys', { method: 'GET' }))?.enc_public_key || null; } catch { pub = null; }
        }
        if (pub) {
            const kp = await generateKeyPair();
            enc = {
                enc_public_key: toBase64url(await exportPublicKey(kp.publicKey)),
                enc_group_privkey: await wrapPrivateKeyForApp(kp.privateKey, pub),
            };
        }
    } catch (err) { console.warn('Group encryption setup at creation skipped:', err); }

    const body = { name, ...enc };
    if (description) body.description = description;
    const group = await api.createGroup(body);

    let photoFailed = false;
    if (image && group?.id) {
        try {
            const res = await api.uploadGroupFile(group.id, image);
            const fileId = (res?.node || res)?.id;
            if (!fileId) throw new Error('upload returned no file');
            await api.setGroupPhoto(group.id, fileId);
        } catch (err) {
            photoFailed = true;
            console.warn('The space was created; its photo did not upload:', err);
        }
    }
    await refreshGroups?.();
    return { group, photoFailed };
}
